import { esEventoInterno, normalizarTipoEvento } from "../utils/eventosInternos";
import React, { useState, useEffect } from "react";

import {
  HiMagnifyingGlass,
  HiInformationCircle,
  HiCheckCircle,
  HiArrowDownTray,
  HiXCircle,
  HiXMark,
  HiChevronLeft,
  HiChevronRight,
  HiArrowPath,
  HiStar,
  HiExclamationCircle,
} from "react-icons/hi2";

const API_URL = "https://desarrollotecnologicoar.com/api5/registro_bonos_pendientes";
const API_URL_EXT = "https://desarrollotecnologicoar.com/api5/bonos_extendidos_pendientes";
const API_URL_APPROVED = "https://desarrollotecnologicoar.com/api5/registro_bonos_aprobados";
const DETAIL_API_URL = "https://desarrollotecnologicoar.com/api5/registro_bonos_desglose/";
const APPROVE_API_URL = "https://desarrollotecnologicoar.com/api5/aprobar_bono/";
const APPROVE_REPORTE = "https://desarrollotecnologicoar.com/api5/reporte_servicio_validado/";
const API_URL_MESES = "https://desarrollotecnologicoar.com/api5/meses_aprobados";
const API_URL_DECLINED = "https://desarrollotecnologicoar.com/api5/rechazar_bono";
const API_URL_RECHAZADOS = "https://desarrollotecnologicoar.com/api5/registro_bonos_rechazados";
const API_URL_REACTIVAR = "https://desarrollotecnologicoar.com/api5/reactivar_bono/";
const API_EDIT_BONO = "https://desarrollotecnologicoar.com/api5/editar_bono/";
const API_EDIT_LOCALIDAD_BONO = "https://desarrollotecnologicoar.com/api5/editar_localidad_bono/";

const meses = [
  { nombre: "Enero", valor: "01" },
  { nombre: "Febrero", valor: "02" },
  { nombre: "Marzo", valor: "03" },
  { nombre: "Abril", valor: "04" },
  { nombre: "Mayo", valor: "05" },
  { nombre: "Junio", valor: "06" },
  { nombre: "Julio", valor: "07" },
  { nombre: "Agosto", valor: "08" },
  { nombre: "Septiembre", valor: "09" },
  { nombre: "Octubre", valor: "10" },
  { nombre: "Noviembre", valor: "11" },
  { nombre: "Diciembre", valor: "12" },
];

/* ------------------------------------------------------------------ */
/*  Pagination component                                              */
/* ------------------------------------------------------------------ */
const Pagination = ({ count, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
      <div className="flex items-center gap-2">
        <span>Filas por página:</span>
        <select
          className="input py-1 px-2 w-20"
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        >
          {[10, 25, 50].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <span>{page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, count)} de {count}</span>
        <button className="btn-icon" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
          <HiChevronLeft className="h-5 w-5" />
        </button>
        <button className="btn-icon" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
          <HiChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Badge de Evaluación de Cliente                                    */
/* ------------------------------------------------------------------ */
const EvaluacionBadge = ({ row }) => {
  const puntuacion = row.evaluacion_puntuacion ?? row.evaluacion?.puntuacion;
  const clientePresente = row.evaluacion_cliente_presente ?? row.evaluacion?.cliente_presente;
  const comentario = row.evaluacion_comentario ?? row.evaluacion?.comentario;

  if (puntuacion != null) {
    return (
      <div className="inline-flex flex-col items-center">
        <span
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-900 border border-amber-200 shadow-sm"
          title={comentario ? `"${comentario}"` : "Sin comentarios"}
        >
          <HiStar className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
          {puntuacion} / 5
        </span>
        {comentario && (
          <span
            className="text-[11px] text-gray-500 italic max-w-[130px] truncate mt-0.5"
            title={comentario}
          >
            "{comentario}"
          </span>
        )}
      </div>
    );
  }

  if (clientePresente === false) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200"
        title="El cliente no estuvo presente durante la finalización del servicio"
      >
        No presente
      </span>
    );
  }

  return <span className="text-gray-400 text-xs">—</span>;
};

/* ================================================================== */
const bonoEsEventoInterno = bono => esEventoInterno(bono?.tipo_servicio) || esEventoInterno(bono?.reserva?.tipo_servicio);
const etiquetaTipo = tipo => esEventoInterno(tipo) ? `${normalizarTipoEvento(tipo)} (sin bono de servicio)` : tipo;

const RegistroBonos = () => {
  const [data, setData] = useState([]);
  const [approvedData, setApprovedData] = useState([]);
  const [rechazadosData, setRechazadosData] = useState([]);
  const [loadingRechazados, setLoadingRechazados] = useState(false);
  const [reactivandoId, setReactivandoId] = useState(null);
  const [pageRechazados, setPageRechazados] = useState(0);
  const [rowsPerPageRechazados, setRowsPerPageRechazados] = useState(10);
  const [tecnicos, setTecnicos] = useState([]);

  const [loadingAprobados, setLoadingAprobados] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pagePendientes, setPagePendientes] = useState(0);
  const [rowsPerPagePendientes, setRowsPerPagePendientes] = useState(10);
  const [pageAprobados, setPageAprobados] = useState(0);
  const [rowsPerPageAprobados, setRowsPerPageAprobados] = useState(10);

  const [search, setSearch] = useState("");
  const [searchAprobados, setSearchAprobados] = useState("");
  const [selectedBono, setSelectedBono] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [mesesPago, setMesesPago] = useState([]);
  const [mesSeleccionado, setMesSeleccionado] = useState("");
  const [mesAprobar, setMesAprobar] = useState("");
  const [folio, setFolio] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReportes, setSelectedReportes] = useState([]);
  const [aclaracionOpen, setAclaracionOpen] = useState(false);
  const [bonoAclarar, setBonoAclarar] = useState(null);
  const [deduccionAclaracion, setDeduccionAclaracion] = useState("");
  const [localidadAclaracion, setLocalidadAclaracion] = useState("Local");
  const [aclarando, setAclarando] = useState(false);

  /* ----------------------- Data fetchers ---------------------- */
  useEffect(() => {
    fetchData();
    fetchApprovedData();
    fetchMesesPago();
    fetchRechazados();
  }, []);

  const fetchData = async () => {
    setLoadingPendientes(true);
    try {
      const response = await fetch(API_URL);
      const result = await response.json();
      const updatedData = await Promise.all(
        result.map(async (item) => {
          const reportResponse = await fetch(`${DETAIL_API_URL}${item.id}`);
          const reportData = await reportResponse.json();
          const evaluacionEnReportes =
            reportData.evaluacion_cliente ||
            reportData.reportes_servicio?.find((r) => r.evaluacion_cliente != null)?.evaluacion_cliente ||
            null;
          return {
            ...item,
            hojas_servicio: reportData.reportes_servicio?.length || 0,
            hojas_ext: reportData.itinerario_ext?.length || 0,
            evaluacion: evaluacionEnReportes,
            evaluacion_puntuacion: item.evaluacion_puntuacion ?? evaluacionEnReportes?.puntuacion,
            evaluacion_cliente_presente: item.evaluacion_cliente_presente ?? evaluacionEnReportes?.cliente_presente,
            evaluacion_comentario: item.evaluacion_comentario ?? evaluacionEnReportes?.comentario,
            evaluacion_fecha: item.evaluacion_fecha ?? evaluacionEnReportes?.fecha_evaluacion,
          };
        })
      );
      const combinedData = updatedData.map((item) => {
        const tecnico = tecnicos.find((t) => t.correo_tecnico === item.correo_tecnico);
        return { ...item, sucursal: tecnico ? tecnico.sucursal : "No disponible" };
      });
      setData(combinedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoadingPendientes(false);
  };

  const fetchApprovedData = async () => {
    setLoadingAprobados(true);
    try {
      const response = await fetch(API_URL_APPROVED);
      const result = await response.json();
      setApprovedData(result);
    } catch (error) {
      console.error("Error fetching approved data:", error);
    }
    setLoadingAprobados(false);
  };

  const fetchRechazados = async () => {
    setLoadingRechazados(true);
    try {
      const response = await fetch(API_URL_RECHAZADOS);
      const result = await response.json();
      setRechazadosData(result);
    } catch (error) {
      console.error("Error fetching rechazados:", error);
    }
    setLoadingRechazados(false);
  };

  const fetchMesesPago = async () => {
    try {
      const response = await fetch(API_URL_MESES);
      const result = await response.json();
      const mesesValidos = result.map((item) => item.mes_pago).filter((mes) => mes !== null);
      setMesesPago(mesesValidos);
    } catch (error) {
      console.error("Error fetching meses de pago:", error);
    }
  };

  /* ----------------------- Tab / search ---------------------- */
  const handleChangeTab = (newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) setPageAprobados(0);
    else if (newValue === 1) setPagePendientes(0);
    else if (newValue === 2) setPageRechazados(0);
  };

  /* ----------------------- Reactivar bono rechazado ---------------------- */
  const handleReactivar = async (id) => {
    if (bonoEsEventoInterno(rechazadosData.find(item => item.id === id))) {
      alert("Este evento de calendario no genera bono de servicio.");
      return;
    }
    if (!window.confirm("¿Regresar este bono a estado Pendiente? Se reiniciarán las hojas validadas.")) return;
    setReactivandoId(id);
    try {
      const response = await fetch(`${API_URL_REACTIVAR}${id}`, { method: "PUT" });
      if (!response.ok) throw new Error("Error al reactivar el bono");
      setRechazadosData((prev) => prev.filter((item) => item.id !== id));
      await fetchData();
    } catch (error) {
      console.error("Error al reactivar el bono:", error);
      alert("No se pudo reactivar el bono. Intenta de nuevo.");
    } finally {
      setReactivandoId(null);
    }
  };

  const handleSearch = (event) => setSearch(event.target.value);

  const handleToggleReporte = (id) => {
    setSelectedReportes((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  /* ----------------------- Details drawer ---------------------- */
  const handleDetails = async (id, hojas) => {
    setLoading(true);
    try {
      const response = await fetch(`${DETAIL_API_URL}${id}`);
      const result = await response.json();
      setSelectedBono({ ...result, no_hojas: hojas });
      setSelectedReportes(result.reportes_servicio?.filter(r => !esEventoInterno(r.tipo_servicio)).map((r) => r.id) ?? []);
      setDrawerOpen(true);
    } catch (error) {
      console.error("Error fetching details:", error);
    }
    setLoading(false);
  };

  /* ----------------------- Approve ---------------------- */
  const handleApprove = async () => {
    if (!selectedBono) return;
    if (bonoEsEventoInterno(selectedBono)) {
      alert("Este evento de calendario no genera bono de servicio.");
      return;
    }
    setApproving(true);
    const bono = {
      ...selectedBono,
      cliente: selectedBono.reserva?.empresa ?? selectedBono.cliente ?? "",
      mes_pago: folio ?? selectedBono.mes_pago ?? "",
    };
    const reportesFiltrados = (bono.reportes_servicio ?? []).filter((r) =>
      selectedReportes.includes(r.id) && !esEventoInterno(r.tipo_servicio)
    );
    const hojas = reportesFiltrados.length;
    const esLocal = bono.localidad === "Local";
    const monto = hojas * (esLocal ? 475 : 950);
    const sancionSum = Number(
      reportesFiltrados
        .reduce((total, r) => total + Number(r?.sancion_puntualidad ?? 0), 0)
        .toFixed(2)
    );
    try {
      const url = `${APPROVE_API_URL}${bono.id}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "Aprobado",
          cliente: bono.cliente,
          no_hojas: hojas,
          monto_aprobado: Number((monto - sancionSum).toFixed(2)),
          suma_sancion: sancionSum,
          localidad: bono.localidad,
          mes_pago: bono.mes_pago,
        }),
      });
      if (!response.ok) throw new Error("Error en la actualización del estado del bono");
      const updatedBono = await response.json();

      await Promise.all(
        reportesFiltrados.map(async (el) => {
          const resp = await fetch(
            `${APPROVE_REPORTE}${APPROVE_REPORTE.endsWith("/") ? "" : "/"}${el.id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ hoja_validada: true, bonos_id: Number(bono.id) }),
            }
          );
          if (!resp.ok) throw new Error(`Error al aprobar la hoja de servicio ${el.id}`);
        })
      );

      setData((prev) => prev.map((i) => (i.id === bono.id ? { ...i, status: "Aprobado" } : i)));
      setApprovedData((prev) => [...prev, updatedBono]);
      setSelectedBono((prev) => (prev ? { ...prev, status: "Aprobado" } : prev));
      setDrawerOpen(false);
      await fetchData();
      await fetchApprovedData();
    } catch (err) {
      console.error("Error al aprobar el bono:", err);
    } finally {
      setApproving(false);
    }
  };

  /* ----------------------- Filtros ---------------------- */
  const filteredData = data.filter((item) => {
    const s = search.toLowerCase().trim();
    return (
      (item.ticket && item.ticket.toString().includes(s)) ||
      (item.nombre_tecnico && item.nombre_tecnico.toLowerCase().includes(s)) ||
      (item.sucursal && item.sucursal.toLowerCase().includes(s))
    );
  });

  /* ----------------------- Aprobados filter ---------------------- */
  const filteredDataAprobados = approvedData
    .filter((row) => (mesSeleccionado ? row.mes_pago === mesSeleccionado : true))
    .filter((row) => {
      const s = searchAprobados.toLowerCase();
      return (
        row.ticket?.toString().includes(s) ||
        row.nombre_tecnico?.toLowerCase().includes(s) ||
        row.cliente?.toLowerCase().includes(s)
      );
    });

  const handleChangeMesPago = (event) => {
    setMesSeleccionado(event.target.value);
    setPageAprobados(0);
  };

  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return "Fecha no disponible";
    const fecha = new Date(fechaISO);
    return new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "short", year: "numeric" })
      .format(fecha)
      .replace(".", "");
  };

  /* ----------------------- CSV download ---------------------- */
  const handleDownloadCSV = () => {
    if (filteredDataAprobados.length === 0) return;
    const headers = [
      "Ticket","Técnico","Cliente","Tipo de Servicio","Localidad",
      "Hojas Aprobadas","Monto","Evaluación Cliente","Comentario Evaluación","Nombre Bonos","Numero de Empleado","Folio",
    ];
    const csvRows = [headers.join(",")];
    filteredDataAprobados.forEach((row) => {
      const formattedMonto = new Intl.NumberFormat("es-MX", {
        style: "currency", currency: "MXN", minimumFractionDigits: 2,
      }).format(row.monto_aprobado).replace(/\$|\s/g, "").replace(",", "");

      const evalPuntuacion = row.evaluacion_puntuacion != null
        ? `${row.evaluacion_puntuacion}/5`
        : (row.evaluacion_cliente_presente === false ? "No presente" : "Sin registrar");
      const evalComentario = (row.evaluacion_comentario || "").replace(/"/g, '""');

      csvRows.push(
        [
          row.ticket,
          `"${row.nombre_tecnico}"`,
          `"${row.cliente}"`,
          row.tipo_servicio,
          row.localidad,
          row.no_hojas,
          `"${formattedMonto}"`,
          `"${evalPuntuacion}"`,
          `"${evalComentario}"`,
          row.nombre_bonos,
          row.numero_empleado,
          row.mes_pago,
        ].join(",")
      );
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bonos_aprobados.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChangeMesAprobar = (event) => {
    const mesValor = event.target.value;
    setMesAprobar(mesValor);
    setFolio(`${new Date().getFullYear()}${mesValor}`);
  };

  /* ----------------------- Rechazar ---------------------- */
  const handleRechazar = async (id) => {
    if (!window.confirm("¿Rechazar este bono? Quedará en la pestaña Rechazados y podrá reactivarse después.")) return;
    try {
      const response = await fetch(`${API_URL_DECLINED}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Rechazado" }),
      });
      if (!response.ok) throw new Error("Error al rechazar el bono");
      setData((prev) => prev.filter((item) => item.id !== id));
      setApprovedData((prev) => prev.filter((item) => item.id !== id));
      setSelectedBono(null);
      setDrawerOpen(false);
    } catch (error) {
      console.error("Error al rechazar el bono:", error);
      alert("Error al rechazar el bono. Por favor, inténtalo de nuevo más tarde.");
    }
  };

  /* ----------------------- Localidad update ---------------------- */
  const handleLocalidadUpdate = async (row, nuevaLocalidad) => {
    setSavingId(row.id);
    try {
      const res = await fetch(`${API_EDIT_LOCALIDAD_BONO}${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ localidad: nuevaLocalidad }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "Error al actualizar la localidad");
        throw new Error(msg);
      }
    } catch (err) {
      console.error("Error actualizando localidad:", err);
      alert("No se pudo actualizar la localidad. Intenta de nuevo.");
    } finally {
      setSavingId(null);
      await fetchData();
      await fetchApprovedData();
    }
  };

  /* ----------------------- Aclaracion handlers ---------------------- */
  const handleOpenAclaracion = (row) => {
    const loc = row.localidad || "Local";
    const tarifa = loc === "Local" ? 475 : 950;
    const tope = row.no_hojas * tarifa;
    const deduccionActual = parseFloat((tope - row.monto_aprobado).toFixed(2));
    setBonoAclarar(row);
    setLocalidadAclaracion(loc);
    setDeduccionAclaracion(deduccionActual >= 0 ? String(deduccionActual) : "0");
    setAclaracionOpen(true);
  };

  const handleAclarar = async () => {
    if (!bonoAclarar) return;
    const tarifa = localidadAclaracion === "Local" ? 475 : 950;
    const tope = bonoAclarar.no_hojas * tarifa;
    const deducVal = parseFloat(deduccionAclaracion);
    if (isNaN(deducVal) || deducVal < 0 || deducVal > tope) return;
    const montoFinal = parseFloat((tope - deducVal).toFixed(2));
    setAclarando(true);
    try {
      const res = await fetch(`${API_EDIT_BONO}${bonoAclarar.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          monto_aprobado: montoFinal,
          suma_sancion: deducVal,
          localidad: localidadAclaracion,
        }),
      });
      if (!res.ok) throw new Error("Error al aclarar el bono");
      setAclaracionOpen(false);
      setBonoAclarar(null);
      await fetchApprovedData();
    } catch (err) {
      console.error("Error al aclarar bono:", err);
      alert("No se pudo aclarar el bono. Intenta de nuevo.");
    } finally {
      setAclarando(false);
    }
  };

  /* ============================================================== */
  /*  TABS config                                                   */
  /* ============================================================== */
  const tabs = [
    { label: "Bonos Aprobados", value: 0 },
    { label: "Bonos Pendientes", value: 1 },
    { label: `Rechazados${rechazadosData.length > 0 ? ` (${rechazadosData.length})` : ""}`, value: 2 },
  ];

  /* ----------------------- Aclaracion computed values ---------------------- */
  const currentMonthFolio = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const tarifaAclaracion = localidadAclaracion === "Local" ? 475 : 950;
  const topeAclaracion = bonoAclarar ? bonoAclarar.no_hojas * tarifaAclaracion : 0;
  const deduccionNum = parseFloat(deduccionAclaracion);
  const deduccionInvalida = isNaN(deduccionNum) || deduccionNum < 0 || deduccionNum > topeAclaracion;
  const montoAclaradoFinal = deduccionInvalida ? 0 : parseFloat((topeAclaracion - deduccionNum).toFixed(2));

  /* ============================================================== */
  /*  Loading placeholder                                           */
  /* ============================================================== */
  const LoadingPlaceholder = () => (
    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
      <img
        src="./gifbono.gif"
        alt="Bono decorativo"
        className="w-60 max-w-full grayscale mb-4"
      />
      <p className="text-sm">Cargando datos...</p>
    </div>
  );

  /* ============================================================== */
  /*  RENDER                                                        */
  /* ============================================================== */
  return (
    <div className="page-container">
      {/* ---------- Header ---------- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="page-title mb-0">Registro de Bonos</h1>

        {activeTab === 0 ? (
          <div className="relative w-full sm:w-96">
            <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              className="input pl-10 pr-10 w-full"
              placeholder="Buscar en Bonos Aprobados (ticket, técnico, cliente)"
              value={searchAprobados}
              onChange={(e) => setSearchAprobados(e.target.value)}
            />
            {searchAprobados && (
              <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchAprobados("")}>
                <HiXMark className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        ) : (
          <div className="relative w-full sm:w-96">
            <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              className="input pl-10 pr-10 w-full"
              placeholder="Buscar por Ticket, Técnico o Sucursal"
              value={search}
              onChange={handleSearch}
            />
            {search && (
              <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch("")}>
                <HiXMark className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ---------- Tabs + controls ---------- */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.value}
              onClick={() => handleChangeTab(t.value)}
              className={`px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap border-b-2 ${
                activeTab === t.value
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 0 && (
          <div className="flex items-center gap-3">
            <select className="input py-2 w-48" value={mesSeleccionado} onChange={handleChangeMesPago}>
              <option value="">Todos los folios</option>
              {mesesPago.map((mes) => (
                <option key={mes} value={mes}>{mes}</option>
              ))}
            </select>
            <button
              className="btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleDownloadCSV}
              disabled={filteredDataAprobados.length === 0}
            >
              <HiArrowDownTray className="h-4 w-4" />
              Descargar CSV
            </button>
          </div>
        )}
      </div>

      {/* ========================== TAB 1: Pendientes ========================== */}
      {activeTab === 1 && (
        loadingPendientes ? <LoadingPlaceholder /> : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="table-header">Ticket</th>
                    <th className="table-header">Estado</th>
                    <th className="table-header">Técnico</th>
                    <th className="table-header">Localidad</th>
                    <th className="table-header">Total hojas (Ext.)</th>
                    <th className="table-header text-center">Evaluación Cliente</th>
                    <th className="table-header">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData
                    .slice(pagePendientes * rowsPerPagePendientes, pagePendientes * rowsPerPagePendientes + rowsPerPagePendientes)
                    .map((row) => (
                      <tr key={`pendiente-${row.id}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="table-cell font-medium">{row.ticket}</td>
                        <td className="table-cell">
                          <span className="badge badge-blue">{row.status}</span>
                        </td>
                        <td className="table-cell">{row.nombre_tecnico}</td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <select
                              className="input py-1 px-2 w-32 text-sm"
                              value={row.localidad || "Local"}
                              onChange={(e) => handleLocalidadUpdate(row, e.target.value)}
                              disabled={savingId === row.id}
                            >
                              <option value="Local">Local</option>
                              <option value="Foráneo">Foráneo</option>
                            </select>
                            {savingId === row.id && (
                              <div className="h-4 w-4 border-2 border-gray-300 border-t-slate-900 rounded-full animate-spin" />
                            )}
                          </div>
                        </td>
                        <td className="table-cell">{`${row.hojas_servicio} (${row.hojas_ext})`}</td>
                        <td className="table-cell text-center">
                          <EvaluacionBadge row={row} />
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-1">
                            <button className="btn-icon" onClick={() => handleDetails(row.id, row.hojas_servicio)}>
                              <HiInformationCircle className="h-5 w-5 text-blue-600" />
                            </button>
                            <button className="btn-icon" title="Rechazar bono" onClick={() => handleRechazar(row.id)}>
                              <HiXCircle className="h-5 w-5 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {filteredData.length > 0 && (
              <Pagination
                count={filteredData.length}
                page={pagePendientes}
                rowsPerPage={rowsPerPagePendientes}
                onPageChange={setPagePendientes}
                onRowsPerPageChange={(v) => { setRowsPerPagePendientes(v); setPagePendientes(0); }}
              />
            )}
          </div>
        )
      )}

      {/* ========================== TAB 2: Rechazados ========================== */}
      {activeTab === 2 && (
        loadingRechazados ? <LoadingPlaceholder /> : (
          <div className="card overflow-hidden">
            {rechazadosData.length === 0 ? (
              <p className="text-sm text-gray-500 p-6 text-center">No hay bonos rechazados.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="table-header">Ticket</th>
                        <th className="table-header">Técnico</th>
                        <th className="table-header">Tipo</th>
                        <th className="table-header">Localidad</th>
                        <th className="table-header">Creado</th>
                        <th className="table-header text-center">Evaluación</th>
                        <th className="table-header">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rechazadosData
                        .slice(pageRechazados * rowsPerPageRechazados, pageRechazados * rowsPerPageRechazados + rowsPerPageRechazados)
                        .map((row) => (
                          <tr key={`rechazado-${row.id}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="table-cell font-medium">{row.ticket}</td>
                            <td className="table-cell">{row.nombre_tecnico}</td>
                            <td className="table-cell">{etiquetaTipo(row.tipo_servicio)}</td>
                            <td className="table-cell">
                              <span className={`badge ${row.localidad === "Local" ? "badge-blue" : "badge-amber"}`}>
                                {row.localidad}
                              </span>
                            </td>
                            <td className="table-cell whitespace-nowrap">
                              {row.created_at ? new Date(row.created_at).toLocaleDateString("es-MX") : "—"}
                            </td>
                            <td className="table-cell text-center">
                              <EvaluacionBadge row={row} />
                            </td>
                            <td className="table-cell">
                              <div className="flex items-center gap-1.5">
                                <button
                                  className="btn-icon"
                                  title="Ver detalles"
                                  onClick={() => handleDetails(row.id, row.no_hojas)}
                                >
                                  <HiInformationCircle className="h-5 w-5 text-blue-600" />
                                </button>
                                <button
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-50"
                                  onClick={() => handleReactivar(row.id)}
                                  disabled={reactivandoId === row.id}
                                >
                                  {reactivandoId === row.id ? (
                                    <div className="h-3.5 w-3.5 border-2 border-amber-400 border-t-amber-800 rounded-full animate-spin" />
                                  ) : (
                                    <HiArrowPath className="h-3.5 w-3.5" />
                                  )}
                                  Reactivar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {rechazadosData.length > rowsPerPageRechazados && (
                  <Pagination
                    count={rechazadosData.length}
                    page={pageRechazados}
                    rowsPerPage={rowsPerPageRechazados}
                    onPageChange={setPageRechazados}
                    onRowsPerPageChange={(v) => { setRowsPerPageRechazados(v); setPageRechazados(0); }}
                  />
                )}
              </>
            )}
          </div>
        )
      )}

      {/* ========================== TAB 0: Aprobados ========================== */}
      {activeTab === 0 && (
        loadingAprobados ? <LoadingPlaceholder /> : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="table-header text-center">Folio</th>
                    <th className="table-header text-center">Ticket</th>
                    <th className="table-header text-center">Técnico</th>
                    <th className="table-header text-center">Cliente</th>
                    <th className="table-header text-center">Tipo de Servicio</th>
                    <th className="table-header text-center">Localidad</th>
                    <th className="table-header text-center">Hojas Aprobadas</th>
                    <th className="table-header text-right">Monto</th>
                    <th className="table-header text-center">Evaluación Cliente</th>
                    <th className="table-header text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDataAprobados
                    .slice(pageAprobados * rowsPerPageAprobados, pageAprobados * rowsPerPageAprobados + rowsPerPageAprobados)
                    .map((row) => (
                      <tr key={`aprobado-${row.id}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="table-cell text-center">{row.mes_pago}</td>
                        <td className="table-cell text-center">{row.ticket}</td>
                        <td className="table-cell text-center">{row.nombre_tecnico}</td>
                        <td className="table-cell text-center">{row.cliente}</td>
                        <td className="table-cell text-center">{etiquetaTipo(row.tipo_servicio)}</td>
                        <td className="table-cell text-center">
                          <span className={`badge ${row.localidad === "Local" ? "badge-blue" : "badge-amber"}`}>
                            {row.localidad}
                          </span>
                        </td>
                        <td className="table-cell text-center">{row.no_hojas}</td>
                        <td className="table-cell text-right font-semibold tabular-nums">
                          {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(row.monto_aprobado)}
                        </td>
                        <td className="table-cell text-center">
                          <EvaluacionBadge row={row} />
                        </td>
                        <td className="table-cell text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              className="btn-icon"
                              title="Ver detalles del bono y hojas de servicio"
                              onClick={() => handleDetails(row.id, row.no_hojas)}
                            >
                              <HiInformationCircle className="h-5 w-5 text-blue-600" />
                            </button>
                            {row.mes_pago === currentMonthFolio && (
                              <button
                                className="btn-icon"
                                title="Aclarar bono"
                                onClick={() => handleOpenAclaracion(row)}
                              >
                                <HiExclamationCircle className="h-5 w-5 text-red-500" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {filteredDataAprobados.length > 0 && (
              <Pagination
                count={filteredDataAprobados.length}
                page={pageAprobados}
                rowsPerPage={rowsPerPageAprobados}
                onPageChange={setPageAprobados}
                onRowsPerPageChange={(v) => { setRowsPerPageAprobados(v); setPageAprobados(0); }}
              />
            )}
          </div>
        )
      )}

      {/* ========================== DRAWER: Detalle Bono ========================== */}
      {drawerOpen && (
        <>
          <div className="overlay" onClick={() => setDrawerOpen(false)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900">Detalles del Bono</h2>
                <button className="btn-icon" onClick={() => setDrawerOpen(false)}>
                  <HiXMark className="h-5 w-5" />
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="h-8 w-8 border-2 border-gray-300 border-t-slate-900 rounded-full animate-spin" />
                </div>
              ) : selectedBono ? (
                <div className="space-y-6">
                  {/* Ticket & Status */}
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-900">Ticket: {selectedBono.ticket}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Estatus:</span>
                      <span className={`badge ${selectedBono.status === "Aprobado" ? "badge-green" : "badge-blue"}`}>
                        {selectedBono.status}
                      </span>
                      {selectedBono.evaluacion_cliente?.puntuacion && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-900 border border-amber-200 ml-auto shadow-sm">
                          <HiStar className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                          {selectedBono.evaluacion_cliente.puntuacion} / 5
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reserva info */}
                  <div>
                    <h3 className="section-title text-sm mb-2">Información de la Reserva</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>Empresa: <span className="text-gray-900">{selectedBono.reserva?.empresa}</span></p>
                      <p>Contacto: <span className="text-gray-900">{selectedBono.reserva?.contacto}</span></p>
                      <p>Equipo: <span className="text-gray-900">{selectedBono.reserva?.equipo}</span></p>
                      <p>Domicilio: <span className="text-gray-900">{selectedBono.reserva?.domicilio}</span></p>
                      <p>Ciudad: <span className="text-gray-900">{selectedBono.reserva?.ciudad}</span></p>
                      <p>Teléfono: <span className="text-gray-900">{selectedBono.reserva?.telefono}</span></p>
                      <p>Fecha de solicitud: <span className="text-gray-900">{formatearFecha(selectedBono.reserva?.fecha_reserva)}</span></p>
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Hojas de servicio */}
                  <div>
                    <h3 className="section-title text-sm mb-1">Hojas de Servicio</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Selecciona los reportes que deseas incluir en esta aprobación.
                    </p>
                    <div className="space-y-4">
                      {selectedBono.reportes_servicio?.map((reporte) => {
                        const checked = selectedReportes.includes(reporte.id);
                        const evalCliente = reporte.evaluacion_cliente || (reporte.evaluacion_id ? {
                          id: reporte.evaluacion_id,
                          reporte_id: reporte.id,
                          cliente_presente: reporte.cliente_presente,
                          puntuacion: reporte.puntuacion,
                          comentario: reporte.evaluacion_comentario,
                          fecha_evaluacion: reporte.fecha_evaluacion,
                        } : null);

                        return (
                          <div
                            key={reporte.id}
                            className={`p-3.5 rounded-xl border-2 transition-all ${
                              checked
                                ? "border-emerald-400 bg-emerald-50/50 shadow-sm"
                                : "border-gray-200 bg-gray-50/70 opacity-70"
                            }`}
                          >
                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 accent-emerald-600 shrink-0 cursor-pointer"
                                checked={checked}
                                onChange={() => handleToggleReporte(reporte.id)}
                              />
                              <div className="space-y-1 w-full">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-bold text-gray-900">
                                    📅 {formatearFecha(reporte.created_at)}
                                  </p>
                                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-gray-200/70 text-gray-600">
                                    Reporte #{reporte.id}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-gray-700">
                                  Hora: {reporte.hora_llegada || "—"} – {reporte.hora_salida || "—"}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>Trabajo Realizado:</strong>{" "}
                                  {reporte.trabajo_realizado?.length > 70
                                    ? `${reporte.trabajo_realizado.slice(0, 70)}...`
                                    : reporte.trabajo_realizado || "N/A"}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>Comentarios:</strong>{" "}
                                  {reporte.comentarios?.length > 70
                                    ? `${reporte.comentarios.slice(0, 70)}...`
                                    : reporte.comentarios || "N/A"}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>Deducción por impuntualidad:</strong>{" "}
                                  {Number(reporte.sancion_puntualidad || 0).toFixed(2)}$
                                </p>
                                {reporte.url_pdf_generado && (
                                  <a
                                    href={reporte.url_pdf_generado}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block text-blue-600 hover:underline text-sm font-semibold mt-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Ver PDF
                                  </a>
                                )}
                              </div>
                            </label>

                            {/* Evaluación del cliente en esta hoja */}
                            {evalCliente && (
                              <div
                                className="mt-3 p-3 bg-amber-50/90 border border-amber-200 rounded-lg text-xs space-y-2 cursor-default"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5 font-bold text-amber-900">
                                    <HiStar className="h-4 w-4 text-amber-500 fill-amber-500" />
                                    <span>Evaluación del Cliente</span>
                                  </div>
                                  <span
                                    className={`px-2 py-0.5 rounded-full font-semibold text-[11px] ${
                                      evalCliente.cliente_presente
                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                        : "bg-gray-100 text-gray-600 border border-gray-200"
                                    }`}
                                  >
                                    {evalCliente.cliente_presente ? "Cliente presente" : "Cliente no presente"}
                                  </span>
                                </div>

                                {evalCliente.cliente_presente ? (
                                  <>
                                    <div className="flex items-center gap-2 pt-0.5">
                                      <div className="flex items-center text-amber-400">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <HiStar
                                            key={star}
                                            className={`h-4 w-4 ${
                                              star <= (evalCliente.puntuacion || 0)
                                                ? "text-amber-400 fill-amber-400"
                                                : "text-gray-300"
                                            }`}
                                          />
                                        ))}
                                      </div>
                                      <span className="font-bold text-gray-900 text-sm">
                                        {evalCliente.puntuacion} / 5
                                      </span>
                                    </div>

                                    {evalCliente.comentario ? (
                                      <div className="bg-white p-2.5 rounded-md border border-amber-100 text-gray-800 italic">
                                        <span className="font-bold text-amber-800 not-italic mr-1.5">
                                          💬 Opinión del cliente:
                                        </span>
                                        "{evalCliente.comentario}"
                                      </div>
                                    ) : (
                                      <p className="text-gray-400 italic">Sin comentarios del cliente</p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-gray-600 italic">
                                    El cliente no estuvo presente durante el cierre de esta hoja de servicio.
                                  </p>
                                )}

                                {evalCliente.fecha_evaluacion && (
                                  <p className="text-[10px] text-gray-400 text-right">
                                    Evaluado el: {new Date(evalCliente.fecha_evaluacion).toLocaleString("es-MX")}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Mes selector */}
                  <div className="max-w-xs space-y-3">
                    <label className="label">Mes</label>
                    <select className="input w-full" value={mesAprobar} onChange={handleChangeMesAprobar}>
                      <option value="">Seleccionar mes</option>
                      {meses.map((mes) => (
                        <option key={mes.valor} value={mes.valor}>{mes.nombre}</option>
                      ))}
                    </select>

                    {folio && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          Folio generado: <span className="font-bold text-gray-900">{folio}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Hojas seleccionadas:{" "}
                          <span className="font-bold text-gray-900">{selectedReportes.length}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Deducciones totales:{" "}
                          <span className="font-bold text-gray-900">
                            {(selectedBono.reportes_servicio ?? [])
                              .filter((r) => selectedReportes.includes(r.id))
                              .reduce((total, r) => total + Number(r.sancion_puntualidad || 0), 0)
                              .toFixed(2)}{" "}
                            $
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Monto a aprobar:{" "}
                          <span className="font-bold text-gray-900">
                            {(
                              selectedReportes.length *
                                (selectedBono.reserva?.localidad === "Local" ? 475 : 950) -
                              (selectedBono.reportes_servicio ?? [])
                                .filter((r) => selectedReportes.includes(r.id))
                                .reduce((total, r) => total + Number(r.sancion_puntualidad || 0), 0)
                            ).toFixed(2)}{" "}
                            $
                          </span>
                        </p>

                        {bonoEsEventoInterno(selectedBono) && <p className="text-sm text-amber-700 mt-3">Evento de calendario: no genera bono de servicio.</p>}
                        {selectedBono.status !== "Aprobado" && (
                          <button
                            className="btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 mt-3"
                            onClick={handleApprove}
                            disabled={approving || selectedReportes.length === 0 || bonoEsEventoInterno(selectedBono)}
                          >
                            <HiCheckCircle className="h-5 w-5" />
                            {approving ? "Aprobando..." : `Aprobar (${selectedReportes.length} hoja${selectedReportes.length !== 1 ? "s" : ""})`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No hay información</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ========================== MODAL: Aclaraciones de Bonos ========================== */}
      {aclaracionOpen && bonoAclarar && (
        <>
          <div className="overlay" onClick={() => setAclaracionOpen(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
              <div className="p-6 space-y-4">
                <h2 className="text-lg font-bold text-gray-900 text-center underline decoration-1">
                  Aclaraciones de bonos
                </h2>

                {/* Card principal */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">Aclaraciones de bonos</span>
                    <span className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">
                      <HiInformationCircle className="h-3.5 w-3.5" />
                      Tope: ${topeAclaracion.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Días de servicio</p>
                      <p className="text-4xl font-bold text-gray-900 leading-none">{bonoAclarar.no_hojas}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <select
                        className="input py-1 px-2 text-xs w-28"
                        value={localidadAclaracion}
                        onChange={(e) => {
                          const newLoc = e.target.value;
                          const oldTarifa = localidadAclaracion === "Local" ? 475 : 950;
                          const newTarifa = newLoc === "Local" ? 475 : 950;
                          const currentDeduc = parseFloat(deduccionAclaracion);
                          if (!isNaN(currentDeduc) && currentDeduc > 0) {
                            const scaled = parseFloat((currentDeduc * newTarifa / oldTarifa).toFixed(2));
                            setDeduccionAclaracion(String(scaled));
                          }
                          setLocalidadAclaracion(newLoc);
                        }}
                      >
                        <option value="Local">Local</option>
                        <option value="Foráneo">Foráneo</option>
                      </select>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                        Tarifa: ${tarifaAclaracion.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="label text-xs mb-1">Deducción</label>
                      <div
                        className={`flex items-center border-2 rounded-lg px-3 py-2 transition-colors ${
                          deduccionInvalida ? "border-red-400 bg-red-50" : "border-gray-200"
                        }`}
                      >
                        <span className="text-gray-400 mr-1 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          max={topeAclaracion}
                          step="0.01"
                          className="w-full outline-none text-sm bg-transparent"
                          value={deduccionAclaracion}
                          onChange={(e) => setDeduccionAclaracion(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label text-xs mb-1">Monto aprobado</label>
                      <div className="flex items-center border-2 border-gray-100 bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-gray-400 mr-1 text-sm">$</span>
                        <span className="text-sm text-gray-700 font-medium">
                          {deduccionInvalida ? "—" : montoAclaradoFinal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resumen */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Tope</p>
                    <p className="font-bold text-gray-900">${topeAclaracion.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Deducción</p>
                    <p className="font-bold text-gray-900">
                      {deduccionInvalida ? "—" : `$${deduccionNum.toFixed(2)}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Aprobado</p>
                    <p className="font-bold text-emerald-700">
                      {deduccionInvalida ? "—" : `$${montoAclaradoFinal.toFixed(2)}`}
                    </p>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-3 pt-1">
                  <button
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => setAclaracionOpen(false)}
                  >
                    Cerrar
                  </button>
                  <button
                    className="btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                    onClick={handleAclarar}
                    disabled={aclarando || deduccionInvalida}
                  >
                    {aclarando ? "Aclarando..." : "Aclarar Bono"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default RegistroBonos;
